import {
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  RoomContext,
  useLocalParticipant,
  useRoomContext,
  useTracks,
} from "@livekit/components-react";
import "@livekit/components-styles";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconMessageCircle,
  IconMicrophone,
  IconMicrophoneOff,
  IconRefresh,
  IconSchool,
  IconUsers,
  IconVideo,
  IconVolume,
  IconVolumeOff,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { Room, Track } from "livekit-client";
import React, { useContext, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/endpoints";
import { subscribeToLiveChatMessages } from "../../Lib/firebase";
import { NonPaginatedResponse } from "../../Lib/responses";
import {
  LessonProps,
  MessageProps,
  RoomProps,
  TeacherProps,
} from "../../Lib/types";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

import { HttpErrorCodesObj } from "../../Lib/data";
import "./styles.scss";

const serverUrl = process.env.REACT_APP_LIVEKIT_SERVER_URL as string;

export default function SingleLessonLiveKit() {
  const { lessonID, batchID } = useParams();

  const navigate = useNavigate();
  const context = useContext(AppContext);

  const {
    isLoading: isLoadingLessonRoom,
    data: lessonRoom,
    response: lessonRoomResponse,
    reloadData: reloadLessonRoom,
    error: lessonRoomError,
  } = usePerformRequest<
    NonPaginatedResponse<{
      room: RoomProps;
      token: string;
      teacher: TeacherProps;
    }>
  >({
    method: Endpoints.GetJoinLessonRoom(lessonID as string, batchID as string)
      .method,
    route: Endpoints.GetJoinLessonRoom(lessonID as string, batchID as string)
      .route,
  });
  const {
    isLoading: isLoadingLesson,
    data: lesson,
    response: lessonResponse,
    reloadData: reloadLesson,
    error: lessonError,
  } = usePerformRequest<NonPaginatedResponse<LessonProps>>({
    method: Endpoints.GetSingleLessonDetails(lessonID as string).method,
    route: Endpoints.GetSingleLessonDetails(lessonID as string).route,
  });

  const [isLessonLive, setLessonLive] = useState<boolean>(true);
  const [messages, setMessages] = useState<MessageProps[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    if (lessonRoomResponse && lessonRoomResponse.status) {
      if (lessonRoomResponse.status === HttpErrorCodesObj.NotFound) {
        setLessonLive(false);
      }
    }
  }, [lessonRoomResponse]);

  // Subscribe to live chat messages
  useEffect(() => {
    if (!lessonID) return;

    const unsubscribe = subscribeToLiveChatMessages(lessonID, (msgs) => {
      setMessages(msgs.sort((a, b) => a.createdAt - b.createdAt));
    });

    return () => unsubscribe();
  }, [lessonID]);

  const [room] = useState(
    () =>
      new Room({
        // Optimize video quality for each participant's screen
        adaptiveStream: true,
        // Enable automatic audio/video quality optimization
        dynacast: true,
      })
  );

  // Connect to room
  useEffect(() => {
    if (lessonRoom && lessonRoom?.data) {
      let mounted = true;

      const connect = async () => {
        if (mounted) {
          await room.connect(serverUrl, lessonRoom?.data?.token as string);

          // Prevent students from publishing video and screen share
          // Students can only use audio
          await room.localParticipant.setCameraEnabled(false);
          await room.localParticipant.setScreenShareEnabled(false);

          // Ensure microphone is enabled by default for students
          await room.localParticipant.setMicrophoneEnabled(true);
        }
      };
      connect();

      //   return () => {
      //     mounted = false;
      //     //   room.disconnect();
      //   };
    }
  }, [lessonRoom, isLoadingLessonRoom]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const formatTime = (timestamp: number) => {
    return dayjs(timestamp).format("h:mm A");
  };

  return (
    <>
      {isLoadingLessonRoom ? (
        // SHOW LESSON LOADER
        <MegaLoader />
      ) : (
        <>
          {isLessonLive && lessonRoom && lessonRoom?.data && (
            <RoomContext.Provider value={room}>
              <div
                data-lk-theme="default"
                style={{ height: "100vh", position: "relative" }}
              >
                {/* Your custom component with basic video conferencing functionality. */}
                <MyVideoConference {...lessonRoom?.data} />
                {/* The RoomAudioRenderer takes care of room-wide audio for you. */}
                <RoomAudioRenderer />
                {/* Custom Controls - Students can only control audio */}
                <StudentControlBar />

                {/* Live Chat Button */}
                {!isChatOpen && (
                  <ActionIcon
                    size="xl"
                    radius="xl"
                    variant="gradient"
                    gradient={{ from: "violet", to: "blue", deg: 90 }}
                    onClick={() => setIsChatOpen(!isChatOpen)}
                    className="live-chat-button"
                    style={{
                      position: "fixed",
                      bottom: 30,
                      right: 30,
                      zIndex: 1000,
                      boxShadow: "0 8px 25px rgba(139, 92, 246, 0.3)",
                    }}
                  >
                    <IconMessageCircle size={23} />
                  </ActionIcon>
                )}

                {/* Live Chat Panel */}
                {isChatOpen && (
                  <Paper
                    className="live-chat-panel"
                    style={{
                      position: "fixed",
                      top: 0,
                      right: 0,
                      width: "500px",
                      height: "100vh",
                      zIndex: 999,
                      display: "flex",
                      flexDirection: "column",
                      background:
                        "linear-gradient(135deg, #0f0f23 0%, #1e1b4b 50%, #312e81 100%)",
                      backdropFilter: "blur(20px)",
                      border: "none",
                      borderRadius: 0,
                    }}
                  >
                    {/* Chat Header */}
                    <Group
                      p="xl"
                      className="chat-header"
                      style={{
                        borderBottom: "2px solid rgba(139, 92, 246, 0.3)",
                        background:
                          "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)",
                        backdropFilter: "blur(20px)",
                      }}
                    >
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #a78bfa 0%, #6366f1 100%)",
                          borderRadius: "8px",
                          width: "48px",
                          height: "48px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 8px 25px rgba(139, 92, 246, 0.4)",
                        }}
                      >
                        <IconMessageCircle size={24} color="white" />
                      </div>
                      <div>
                        <Text
                          fw={700}
                          size="xl"
                          c="white"
                          style={{
                            background:
                              "linear-gradient(135deg, #a78bfa 0%, #6366f1 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          Live Chat
                        </Text>
                        <Text size="sm" c="gray.3">
                          Real-time conversation with your class
                        </Text>
                      </div>
                      <ActionIcon
                        variant="gradient"
                        gradient={{ from: "red", to: "pink", deg: 45 }}
                        size="lg"
                        radius="md"
                        onClick={() => setIsChatOpen(false)}
                        style={{ marginLeft: "auto" }}
                      >
                        <IconArrowRight size={20} />
                      </ActionIcon>
                    </Group>

                    {/* Messages Area */}
                    <ScrollArea
                      flex={1}
                      p="xl"
                      className="messages-area"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(15, 15, 35, 0.8) 0%, rgba(30, 27, 75, 0.8) 100%)",
                      }}
                    >
                      <Stack gap="lg">
                        {messages.length === 0 ? (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "60px 20px",
                            }}
                          >
                            <div
                              style={{
                                background:
                                  "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)",
                                borderRadius: "8px",
                                width: "120px",
                                height: "120px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 20px",
                                border: "2px solid rgba(139, 92, 246, 0.2)",
                              }}
                            >
                              <IconMessageCircle size={48} color="#a78bfa" />
                            </div>
                            <Text size="lg" c="gray.3" fw={600}>
                              No messages yet
                            </Text>
                            <Text size="sm" c="gray.5" mt={8}>
                              Start the conversation with your classmates!
                            </Text>
                          </div>
                        ) : (
                          messages.map((msg) => (
                            <Group
                              key={msg.id}
                              align="flex-end"
                              justify={
                                msg.senderID === context?.profile?.id
                                  ? "flex-end"
                                  : "flex-start"
                              }
                            >
                              <Stack
                                gap={6}
                                align={
                                  msg.senderID === context?.profile?.id
                                    ? "flex-end"
                                    : "flex-start"
                                }
                              >
                                {/* Sender Name */}
                                <Group gap={8} align="center">
                                  <div
                                    className="sender-indicator"
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      background: msg.isTeacher
                                        ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                                        : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                    }}
                                  />
                                  <Text
                                    size="xs"
                                    c="gray.4"
                                    fw={600}
                                    style={{
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    {msg?.name ||
                                      (msg.isTeacher ? "Teacher" : "Student")}
                                  </Text>
                                </Group>

                                {/* Message Bubble */}
                                <Paper
                                  radius="sm"
                                  p="sm"
                                  className={`message-bubble ${
                                    msg.senderID === context?.profile?.id
                                      ? "own-message"
                                      : "other-message"
                                  }`}
                                  style={{
                                    maxWidth: 280,
                                    minWidth: 120,
                                  }}
                                >
                                  <Text
                                    size="sm"
                                    c={
                                      msg.senderID === context?.profile?.id
                                        ? "white"
                                        : "gray.2"
                                    }
                                    lh={1.4}
                                  >
                                    {msg.message}
                                  </Text>
                                  <Text
                                    size="xs"
                                    c={
                                      msg.senderID === context?.profile?.id
                                        ? "rgba(255,255,255,0.6)"
                                        : "gray.5"
                                    }
                                    mt={4}
                                    ta="right"
                                  >
                                    {formatTime(msg.createdAt)}
                                  </Text>
                                </Paper>
                              </Stack>
                            </Group>
                          ))
                        )}
                      </Stack>
                    </ScrollArea>
                  </Paper>
                )}
              </div>
            </RoomContext.Provider>
          )}
          {!isLessonLive && (
            // BEAUTIFUL LESSON NOT LIVE UI
            <div
              style={{
                minHeight: "100vh",
                background:
                  "linear-gradient(135deg, #0f0f23 0%, #1e1b4b 50%, #312e81 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "2rem",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Animated background elements */}
              <div
                style={{
                  position: "absolute",
                  top: "10%",
                  left: "10%",
                  width: "200px",
                  height: "200px",
                  background:
                    "radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)",
                  borderRadius: "50%",
                  animation: "float 6s ease-in-out infinite",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "20%",
                  right: "15%",
                  width: "150px",
                  height: "150px",
                  background:
                    "radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)",
                  borderRadius: "50%",
                  animation: "float 8s ease-in-out infinite reverse",
                }}
              />

              <Container size="sm">
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Card
                    radius="xl"
                    padding="xl"
                    style={{
                      background: "rgba(26, 26, 46, 0.8)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(139, 92, 246, 0.2)",
                      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    <Stack align="center">
                      {/* Header with back button */}
                      <Group w="100%">
                        <ActionIcon
                          size="lg"
                          variant="subtle"
                          onClick={handleGoBack}
                          style={{
                            color: "#a78bfa",
                            background: "rgba(139, 92, 246, 0.1)",
                            border: "1px solid rgba(139, 92, 246, 0.2)",
                          }}
                        >
                          <IconArrowLeft size={20} />
                        </ActionIcon>
                        <Badge
                          size="lg"
                          variant="gradient"
                          gradient={{ from: "violet", to: "blue", deg: 90 }}
                          radius="xl"
                          px={16}
                          py={8}
                        >
                          Waiting for teacher to start the lesson
                        </Badge>
                      </Group>

                      {/* Main icon */}
                      <motion.div
                        animate={{
                          scale: [1, 1.05, 1],
                          rotate: [0, 5, -5, 0],
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        style={{
                          width: "120px",
                          height: "120px",
                          background:
                            "linear-gradient(135deg, #a78bfa 0%, #6366f1 100%)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 20px 40px rgba(139, 92, 246, 0.3)",
                        }}
                      >
                        <IconVideo size={48} color="white" />
                      </motion.div>

                      {/* Title */}
                      <Title
                        order={1}
                        size="2.5rem"
                        ta="center"
                        style={{
                          background:
                            "linear-gradient(135deg, #a78bfa 0%, #6366f1 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          fontWeight: 900,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {lesson?.data?.title}
                      </Title>

                      {/* Description */}
                      <Text size="lg" ta="center" c="gray.3" maw={500} lh={1.6}>
                        {lesson?.data?.description}
                      </Text>

                      {/* Info cards */}
                      <Group w="100%">
                        <Card
                          radius="md"
                          padding="md"
                          style={{
                            background: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            flex: 1,
                          }}
                        >
                          <Stack align="center">
                            <IconUsers size={24} color="#a78bfa" />
                            <Text size="sm" c="gray.4" ta="center">
                              Students
                            </Text>
                            <Text size="lg" fw={700} c="white">
                              --
                            </Text>
                          </Stack>
                        </Card>
                        <Card
                          radius="md"
                          padding="md"
                          style={{
                            background: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            flex: 1,
                          }}
                        >
                          <Stack align="center">
                            <IconSchool size={24} color="#a78bfa" />
                            <Text size="sm" c="gray.4" ta="center">
                              {lessonRoom?.data?.teacher?.name}
                            </Text>
                            <Text size="lg" fw={700} c="white">
                              {
                                //@ts-ignore
                                lessonRoomError?.response?.data?.data?.teacher
                                  ?.name ?? "--"
                              }
                            </Text>
                          </Stack>
                        </Card>
                      </Group>

                      {/* Action buttons */}
                      <Group w="100%">
                        <Button
                          variant="gradient"
                          gradient={{ from: "violet", to: "blue", deg: 90 }}
                          size="lg"
                          radius="xl"
                          leftSection={<IconRefresh size={20} />}
                          onClick={reloadLessonRoom}
                          fullWidth
                          style={{
                            fontWeight: 700,
                            boxShadow: "0 8px 25px rgba(139, 92, 246, 0.3)",
                          }}
                        >
                          Refresh
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                </motion.div>
              </Container>
            </div>
          )}
        </>
      )}
    </>
  );
}

// Custom Control Bar for Students - Only Audio Controls
const StudentControlBar: React.FC = () => {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isMuted, setIsMuted] = useState(false);

  const toggleAudio = async () => {
    if (localParticipant.isMicrophoneEnabled) {
      await localParticipant.setMicrophoneEnabled(false);
      setIsAudioEnabled(false);
    } else {
      await localParticipant.setMicrophoneEnabled(true);
      setIsAudioEnabled(true);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    // This would typically control the audio output
    // For now, we'll just toggle the state
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        display: "flex",
        gap: "12px",
        alignItems: "center",
        background: "rgba(0, 0, 0, 0.8)",
        backdropFilter: "blur(20px)",
        padding: "12px 20px",
        borderRadius: "50px",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
      }}
    >
      {/* Audio Control */}
      <Tooltip label={isAudioEnabled ? "Mute Microphone" : "Unmute Microphone"}>
        <ActionIcon
          size="lg"
          radius="xl"
          variant={isAudioEnabled ? "filled" : "light"}
          color={isAudioEnabled ? "green" : "red"}
          onClick={toggleAudio}
          style={{
            background: isAudioEnabled
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
              : "rgba(239, 68, 68, 0.2)",
            border: isAudioEnabled
              ? "none"
              : "1px solid rgba(239, 68, 68, 0.3)",
          }}
        >
          {isAudioEnabled ? (
            <IconMicrophone size={20} />
          ) : (
            <IconMicrophoneOff size={20} />
          )}
        </ActionIcon>
      </Tooltip>

      {/* Volume Control */}
      <Tooltip label={isMuted ? "Unmute Speakers" : "Mute Speakers"}>
        <ActionIcon
          size="lg"
          radius="xl"
          variant={isMuted ? "light" : "filled"}
          color={isMuted ? "red" : "blue"}
          onClick={toggleMute}
          style={{
            background: isMuted
              ? "rgba(239, 68, 68, 0.2)"
              : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            border: isMuted ? "1px solid rgba(239, 68, 68, 0.3)" : "none",
          }}
        >
          {isMuted ? <IconVolumeOff size={20} /> : <IconVolume size={20} />}
        </ActionIcon>
      </Tooltip>

      {/* Disabled Video Indicator */}
      <Tooltip label="Video is disabled for students">
        <ActionIcon
          size="lg"
          radius="xl"
          variant="light"
          color="gray"
          disabled
          style={{
            background: "rgba(107, 114, 128, 0.2)",
            border: "1px solid rgba(107, 114, 128, 0.3)",
            cursor: "not-allowed",
            opacity: 0.5,
          }}
        >
          <IconVideo size={20} />
        </ActionIcon>
      </Tooltip>

      {/* Disabled Screen Share Indicator */}
      <Tooltip label="Screen sharing is disabled for students">
        <ActionIcon
          size="lg"
          radius="xl"
          variant="light"
          color="gray"
          disabled
          style={{
            background: "rgba(107, 114, 128, 0.2)",
            border: "1px solid rgba(107, 114, 128, 0.3)",
            cursor: "not-allowed",
            opacity: 0.5,
          }}
        >
          <IconVideo size={20} />
        </ActionIcon>
      </Tooltip>

      {/* Student Mode Indicator */}
      <div
        style={{
          background: "rgba(139, 92, 246, 0.2)",
          border: "1px solid rgba(139, 92, 246, 0.3)",
          borderRadius: "20px",
          padding: "4px 12px",
          fontSize: "12px",
          color: "#a78bfa",
          fontWeight: 600,
        }}
      >
        Student Mode
      </div>
    </div>
  );
};

const MyVideoConference: React.FC<{
  room: RoomProps;
  token: string;
  teacher: TeacherProps;
}> = (data) => {
  const context = useContext(AppContext);
  // `useTracks` returns all camera and screen share tracks. If a user
  // joins without a published camera track, a placeholder track is returned.
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Find any screen share track (not just from teacher)
  const anyScreenShare = tracks.find(
    (t) => t.publication && t.publication.source === Track.Source.ScreenShare
  );

  // Find all screen shares
  const allScreenShares = tracks.filter(
    (t) => t.publication && t.publication.source === Track.Source.ScreenShare
  );

  // Find teacher's screen share specifically
  const teacherScreenShare = tracks.find(
    (t) =>
      t.publication &&
      t.publication.source === Track.Source.ScreenShare &&
      t.participant.identity === data?.teacher?.name
  );

  // Use any screen share if teacher's specific one isn't found
  const activeScreenShare = teacherScreenShare || anyScreenShare;
  const screenShareOwner = activeScreenShare?.participant.identity || "Unknown";

  const teacherCamera = tracks.find(
    (t) =>
      t?.publication &&
      t?.publication?.source === Track?.Source?.Camera &&
      t?.participant?.identity === data?.teacher?.name
  );

  return (
    <>
      {activeScreenShare ? (
        <>
          {/* Screen Sharing Active - Full Screen Layout */}
          <div className="screen-share-container">
            {/* Screen Share Display */}
            <div className="screen-share-main">
              <div className="screen-share-header">
                <Badge
                  color="green"
                  variant="filled"
                  size="lg"
                  leftSection={<IconVideo size={14} />}
                >
                  {screenShareOwner} is sharing screen
                </Badge>
              </div>
              <div className="screen-share-content">
                {/* @ts-ignore */}
                <ParticipantTile
                  style={{ width: "100%", height: "100%" }}
                  trackRef={activeScreenShare}
                />
                {/* Fallback if ParticipantTile doesn't work */}
              </div>
            </div>

            {/* Teacher Camera Overlay */}
            {teacherCamera && (
              <div className="teacher-camera-overlay">
                <div className="camera-overlay-header">
                  <Badge color="blue" variant="light" size="sm">
                    {data?.teacher?.name}
                  </Badge>
                </div>
                {/* @ts-ignore */}
                <ParticipantTile
                  style={{ width: "100%", height: "100%" }}
                  trackRef={teacherCamera}
                />
              </div>
            )}

            {/* Other Participants Grid (if any) */}
            <div className="other-participants-grid">
              <GridLayout
                tracks={tracks.filter(
                  (t) =>
                    t.publication?.source !== Track.Source.ScreenShare &&
                    t?.participant?.identity !== data?.teacher?.name
                )}
              >
                {/* @ts-ignore */}
                <ParticipantTile style={{ width: "200px", height: "150px" }} />
              </GridLayout>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Normal Layout - No Screen Sharing */}
          <div className="normal-layout">
            {/* Screen Sharing Status Indicator */}
            <div className="no-screen-share-indicator">
              <Badge
                color="gray"
                variant="light"
                size="lg"
                leftSection={<IconVideo size={14} />}
              >
                No screen sharing active
              </Badge>
            </div>

            <GridLayout
              tracks={tracks.filter(
                (t) => t.publication?.source !== Track.Source.ScreenShare
              )}
            >
              {/* @ts-ignore */}
              <ParticipantTile style={{ width: "300px", height: "300px" }} />
            </GridLayout>
          </div>
        </>
      )}
    </>
  );
};

// Add CSS for floating animation
const style = document.createElement("style");
style.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
`;
document.head.appendChild(style);
